<template>
  <div v-show="!paypal.onApprove.success && !paypal.onApprove.error">
    <div>
      <form ref="form" action="#" method="post" @submit.prevent>
        <section class="mb-5">
          <h3 class="text-center">{{ $t("personalDetails") }}</h3>

          <div class="form-floating mb-4">
            <input
              id="firstname"
              v-model="form.firstname"
              :class="form.firstnameClass"
              :disabled="paypal.processPending"
              class="form-control"
              :placeholder="$t('firstname')"
              type="text"
            />
            <label for="firstname">{{ $t("firstname") }}</label>
            <div class="invalid-feedback">{{ $t("requiredField") }}</div>
          </div>

          <div class="form-floating">
            <input
              id="lastname"
              v-model="form.lastname"
              :class="form.lastnameClass"
              :disabled="paypal.processPending"
              class="form-control"
              :placeholder="$t('surname')"
              type="text"
            />
            <label for="lastname">{{ $t("surname") }}</label>
            <div class="invalid-feedback">{{ $t("requiredField") }}</div>
          </div>
        </section>

        <section class="mb-5">
          <h3 class="text-center">{{ $t("address") }}</h3>

          <div class="form-floating mb-4">
            <input
              id="street"
              v-model="form.address.street"
              :class="form.address.streetClass"
              :disabled="paypal.processPending"
              class="form-control"
              :placeholder="$t('streetNumber')"
              type="text"
            />
            <label for="street">{{ $t("streetNumber") }}</label>
            <div class="invalid-feedback">
              {{ $t("requiredField") }}
            </div>
          </div>

          <div class="form-floating mb-4">
            <input
              id="postalCode"
              v-model="form.address.postalCode"
              :class="form.address.postalCodeClass"
              :disabled="paypal.processPending"
              class="form-control"
              :placeholder="$t('zipCode')"
              type="text"
            />
            <label for="postalCode">{{ $t("zipCode") }}</label>
            <div class="invalid-feedback">{{ $t("requiredField") }}</div>
          </div>

          <div class="form-floating mb-4">
            <input
              id="city"
              v-model="form.address.city"
              :class="form.address.cityClass"
              :disabled="paypal.processPending"
              class="form-control"
              :placeholder="$t('city')"
              type="text"
            />
            <label for="city">{{ $t("city") }}</label>
            <div class="invalid-feedback">
              {{ $t("requiredField") }}
            </div>
          </div>

          <div class="form-floating">
            <input
              id="country"
              v-model="form.address.country"
              :class="form.address.countryClass"
              :disabled="paypal.processPending"
              class="form-control"
              :placeholder="$t('country')"
              type="text"
            />
            <label for="country">{{ $t("country") }}</label>
            <div class="invalid-feedback">{{ $t("requiredField") }}</div>
          </div>
        </section>

        <section class="mb-5">
          <h3 class="text-center">{{ $t("affiliateProgram") }}</h3>

          <div v-if="productPeriod !== 360">
            <div class="alert alert-danger mb-4">
              {{ $t("affiliateOnly360Days") }}
            </div>
            <router-link
              :to="{
                name: 'membership.extend.360',
                params: {
                  firstname: this.form.firstname,
                  lastname: this.form.lastname,
                  street: this.form.address.street,
                  postalCode: this.form.address.postalCode,
                  city: this.form.address.city,
                  country: this.form.address.country,
                },
              }"
              class="btn btn-secondary w-100"
              >{{ $t("participateNow") }}
            </router-link>
          </div>

          <div v-else-if="showAffiliate">
            <div class="form-floating mb-4">
              <input
                id="affiliate"
                v-model="form.affiliate"
                :class="form.affiliateClass"
                :disabled="paypal.processPending || form.affiliateCheck"
                class="form-control"
                :placeholder="$t('affiliateEmail')"
                type="text"
              />
              <label for="affiliate">{{ $t("affiliateEmail") }}</label>
              <div v-if="!!form.affiliateValid" class="valid-feedback">
                {{ $t("affiliateSuccess") }}
              </div>
              <div v-else class="valid-feedback">
                {{ $t("affiliateNotChecked") }}
              </div>
              <div v-if="!form.affiliateValid" class="invalid-feedback">
                {{ $t("affiliateError") }}
              </div>
              <div v-else class="invalid-feedback">
                {{ $t("errorInvalidEmail") }}
              </div>
            </div>

            <button
              :disabled="
                form.affiliateCheck ||
                !form.affiliateChanged ||
                form.affiliateClass !== 'is-valid'
              "
              class="btn btn-light w-100"
              type="button"
              @click.prevent="checkAffiliate"
            >
              <spinner v-if="form.affiliateCheck" />
              <span v-else>{{ $t("verify") }}</span>
            </button>
          </div>

          <div v-else class="p-4 border border-secondary rounded">
            {{ $t("affiliateOnlyFirstTime") }}
          </div>
        </section>

        <section>
          <h3 class="text-center">{{ $t("consent") }}</h3>

          <div class="form-check mb-4 align-items-center">
            <input
              id="privacy"
              v-model="form.privacy"
              :class="form.privacyClass"
              :disabled="paypal.processPending"
              class="form-check-input"
              type="checkbox"
            />
            <label class="form-check-label d-inline" for="privacy">
              <i18n-t keypath="acceptPrivacyPolice" tag="span">
                <a
                  href="https://numbersofsuccess.com/privacy"
                  target="_blank"
                  >{{ $t("privacy") }}</a
                >
              </i18n-t>
            </label>
            <div class="invalid-feedback">
              {{ $t("errorPrivacy") }}
            </div>
          </div>

          <div class="form-check align-items-center">
            <input
              id="terms"
              v-model="form.terms"
              :class="form.termsClass"
              :disabled="paypal.processPending"
              class="form-check-input"
              type="checkbox"
            />
            <label class="form-check-label d-inline" for="terms">
              <i18n-t keypath="acceptTerms" tag="span">
                <a href="https://numbersofsuccess.com/terms" target="_blank">{{
                  $t("terms")
                }}</a>
              </i18n-t>
            </label>
            <div class="invalid-feedback">{{ $t("errorTerms") }}</div>
          </div>
        </section>
      </form>

      <section>
        <!-- onCancel -->
        <div v-if="paypal.canceled" class="alert alert-danger">
          {{ $t("errorPaymentFormCancelled") }}
        </div>
      </section>

      <section class="mt-5">
        <div
          v-show="initialized && !paypal.onApprove.loading"
          class="bg-white rounded p-3"
        >
          <div id="paypalbtn"></div>
        </div>

        <div v-if="initError" class="alert alert-danger">
          {{ $t("errorPaymentFormPaypalConnection") }}
        </div>

        <spinner v-show="!initialized && !initError" centered />

        <spinner v-show="paypal.onApprove.loading" centered />
      </section>
    </div>
  </div>

  <!-- onApprove Success -->
  <div v-if="paypal.onApprove.success" class="alert alert-success">
    {{ $t("paymentSuccess") }}
    <router-link class="text-white" :to="{ name: 'profile' }">{{
      $t("returnProfilePage")
    }}</router-link>
  </div>

  <!-- onApprove Error -->
  <div v-if="paypal.onApprove.error" class="alert alert-danger">
    {{ $t("errorPaymentForm") }}
  </div>
</template>

<script>
import { loadScript } from "@paypal/paypal-js";
import functions from "@/firebase/functions";
import { httpsCallable } from "firebase/functions";
import Spinner from "@/components/Spinner";

export default {
  name: "PaymentForm",
  components: {
    Spinner,
  },

  props: {
    productId: {
      type: String,
      required: true,
    },
    productPeriod: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      initialized: false,
      initError: false,
      formDefault: {
        firstname: null,
        firstnameClass: null,
        lastname: null,
        lastnameClass: null,
        address: {
          street: null,
          streetClass: null,
          postalCode: null,
          postalCodeClass: null,
          city: null,
          cityClass: null,
          country: null,
          countryClass: null,
        },
        affiliate: null,
        affiliateChanged: false,
        affiliateClass: null,
        affiliateCheck: false,
        affiliateNotVerified: false,
        affiliateValid: false,
        privacy: false,
        privacyClass: null,
        terms: false,
        termsClass: null,
      },
      form: {
        firstname: null,
        firstnameClass: null,
        lastname: null,
        lastnameClass: null,
        address: {
          street: null,
          streetClass: null,
          postalCode: null,
          postalCodeClass: null,
          city: null,
          cityClass: null,
          country: null,
          countryClass: null,
        },
        affiliate: null,
        affiliateChanged: false,
        affiliateClass: null,
        affiliateCheck: false,
        affiliateNotVerified: false,
        affiliateValid: false,
        privacy: false,
        privacyClass: null,
        terms: false,
        termsClass: null,
      },
      paypal: {
        processPending: false,
        canceled: false,
        onApprove: {
          success: false,
          error: false,
          loading: false,
        },
      },
    };
  },
  watch: {
    "form.firstname": function () {
      this.form.firstnameClass = null;
    },
    "form.lastname": function () {
      this.form.lastnameClass = null;
    },
    "form.address.street": function () {
      this.form.address.streetClass = null;
    },
    "form.address.postalCode": function () {
      this.form.address.postalCodeClass = null;
    },
    "form.address.city": function () {
      this.form.address.cityClass = null;
    },
    "form.address.country": function () {
      this.form.address.countryClass = null;
    },
    "form.affiliate": function (newValue, oldValue) {
      this.form.affiliateValid = false;
      this.form.affiliateNotVerified = false;

      if (!newValue) {
        this.form.affiliateClass = null;
      } else {
        if (newValue !== oldValue) {
          this.form.affiliateChanged = true;
        }

        if (this.validateEmail(newValue)) {
          this.form.affiliateClass = "is-valid";
        } else {
          this.form.affiliateClass = "is-invalid";
        }
      }
    },
    "form.privacy": function (newValue) {
      if (newValue === true) {
        this.form.privacyClass = null;
      } else {
        this.form.privacyClass = "is-invalid";
      }
    },
    "form.terms": function (newValue) {
      if (newValue === true) {
        this.form.termsClass = null;
      } else {
        this.form.termsClass = "is-invalid";
      }
    },
  },
  computed: {
    showAffiliate() {
      return this.$store.getters["membership/getAffiliateAuthorized"];
    },
  },
  methods: {
    async checkAffiliate() {
      this.form.affiliateClass = null;
      this.form.affiliateCheck = true;
      this.form.affiliateChanged = false;

      const _this = this;

      const validateAffiliate = httpsCallable(
        functions,
        "membership-affiliate-validate"
      );
      await validateAffiliate(this.form.affiliate)
        .then((result) => {
          if (result.data) {
            _this.form.affiliateValid = true;
            _this.form.affiliateClass = "is-valid";
          } else {
            _this.form.affiliateValid = false;
            _this.form.affiliateClass = "is-invalid";
          }
        })
        .catch(() => {
          _this.form.affiliateValid = false;
          _this.form.affiliateClass = "is-invalid";
        });

      this.form.affiliateCheck = false;
    },
    validate() {
      let $return = true;

      if (
        this.form.firstname === null ||
        this.form.firstname.replace(" ", "").length === 0
      ) {
        $return = false;
        this.form.firstnameClass = "is-invalid";
      } else {
        this.form.firstnameClass = "is-valid";
      }

      if (
        this.form.lastname === null ||
        this.form.lastname.replace(" ", "").length === 0
      ) {
        $return = false;
        this.form.lastnameClass = "is-invalid";
      } else {
        this.form.lastnameClass = "is-valid";
      }

      if (!/\d/.test(this.form.address.street)) {
        $return = false;
        this.form.address.streetClass = "is-invalid";
      } else {
        this.form.address.streetClass = "is-valid";
      }

      if (
        this.form.address.postalCode === null ||
        this.form.address.postalCode.replace(" ", "").length === 0
      ) {
        $return = false;
        this.form.address.postalCodeClass = "is-invalid";
      } else {
        this.form.address.postalCodeClass = "is-valid";
      }

      if (
        this.form.address.city === null ||
        this.form.address.city.replace(" ", "").length === 0
      ) {
        $return = false;
        this.form.address.cityClass = "is-invalid";
      } else {
        this.form.address.cityClass = "is-valid";
      }

      if (
        this.form.address.country === null ||
        this.form.address.country.replace(" ", "").length === 0
      ) {
        $return = false;
        this.form.address.countryClass = "is-invalid";
      } else {
        this.form.address.countryClass = "is-valid";
      }

      if (
        this.form.affiliate !== null &&
        this.form.affiliate.replace(" ", "").length > 0
      ) {
        if (this.form.affiliateValid !== true) {
          $return = false;
        }
        if (this.form.affiliateChanged) {
          this.form.affiliateNotVerified = true;
          this.form.affiliateClass = "is-invalid";
        }
      }

      if (!this.form.privacy) {
        $return = false;
        this.form.privacyClass = "is-invalid";
      } else {
        this.form.privacyClass = null;
      }

      if (!this.form.terms) {
        $return = false;
        this.form.termsClass = "is-invalid";
      } else {
        this.form.termsClass = null;
      }

      return $return;
    },
  },
  async mounted() {
    const _this = this;

    if (this.$route.params.firstname) {
      const params = this.$route.params;
      this.form.firstname = params.firstname;
      this.form.lastname = params.lastname;
      this.form.address.street = params.street;
      this.form.address.postalCode = params.postalCode;
      this.form.address.city = params.city;
      this.form.address.country = params.country;
    }

    if (this.$store.getters["membership/getLastAddress"] !== null) {
      const lastAddress = this.$store.getters["membership/getLastAddress"];

      this.form.firstname = lastAddress.firstname;
      this.form.lastname = lastAddress.lastname;
      this.form.address = lastAddress.address;
    }

    loadScript({
      "client-id":"AfDwP3g2yzyyITQI15agDr4m2MBrBlDpbO9a9B0w2RM4uEORAqLh-Nv23AoVS513EhCNpJwvHxMcad-c" ,
      currency: "EUR",
      commit: true,
      vault: false,
    }).then((paypal) => {
      paypal
        .Buttons({
          style: {
            layout: "vertical",
            color: "blue",
            shape: "pill",
            label: "paypal",
          },

          onInit() {},

          onClick(data, actions) {
            if (!_this.validate()) {
              return actions.reject();
            }
            _this.paypal.processPending = true;
            return actions.resolve();
          },

          createOrder() {
            const createOrder = httpsCallable(
              functions,
              "membership-paypal-createOrder"
            );

            return createOrder({
              productId: _this.productId,
              firstname: _this.form.firstname,
              lastname: _this.form.lastname,
              address: {
                street: _this.form.address.street,
                city: _this.form.address.city,
                postalCode: _this.form.address.postalCode,
                country: _this.form.address.country,
              },
              affiliate: _this.form.affiliate,
              agreements: {
                privacy: _this.form.privacy,
                terms: _this.form.terms,
              },
            }).then((result) => result.data);
          },
          async onCancel(data) {
            const deleteOrder = httpsCallable(
              functions,
              "membership-paypal-deleteOrder"
            );

            await deleteOrder({
              order_id: data.orderID,
            });

            _this.form.privacy = false;
            _this.form.terms = false;
            _this.paypal.canceled = true;
            _this.paypal.processPending = false;
          },
          async onError(error) {
            const deleteOrder = httpsCallable(
              functions,
              "membership-paypal-deleteOrder"
            );

            await deleteOrder({
              order_id: error.orderID,
            });

            _this.form.privacy = false;
            _this.form.terms = false;
            _this.paypal.canceled = true;
            _this.paypal.processPending = false;
          },
          onApprove(data) {
            const validateOrder = httpsCallable(
              functions,
              "membership-paypal-validateOrder"
            );
            _this.paypal.onApprove.loading = true;

            validateOrder({
              order_id: data.orderID,
            })
              .then((result) => {
                if (result.data === true) {
                  _this.$store.dispatch("membership/getMembershipFromFirebase");
                  _this.form = _this.formDefault;
                  _this.paypal.onApprove.loading = false;
                  _this.paypal.onApprove.success = true;
                } else {
                  _this.paypal.onApprove.error = true;
                }
                _this.paypal.processPending = false;
              })
              .catch(() => {
                _this.paypal.onApprove.error = true;
                _this.paypal.processPending = false;
              });
          },
        })
        .render("#paypalbtn")
        .then(() => {
          _this.initialized = true;
        });
    });
  },
  beforeUnmount() {
    document.getElementById("paypalbtn").innerHTML = "";
  },
};
</script>

<style scoped></style>
